import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Title2,
  Body1,
  Spinner,
  Badge,
  Button,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import NewRequestDrawer, { LeaveTypeOption } from "../components/NewRequestDrawer";
import BalancePanel from "../components/BalancePanel";
import { fetchWithRetry } from "../lib/fetchWithRetry";

interface LeaveRequest {
  id: string;
  requestNumber: number;
  leaveTypeName: string;
  status: "draft" | "pending" | "approved" | "denied" | "cancelled";
  startDate: string;
  endDate: string;
  workDays: number;
  halfDay: boolean;
  amPm: "AM" | "PM" | null;
  notes: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  denialReason: string | null;
}

interface ApiResponse {
  userId: string;
  userDisplayName: string;
  requests: LeaveRequest[];
}

const useStyles = makeStyles({
  headerRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalS
  },
  empty: {
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium
  },
  error: {
    color: tokens.colorPaletteRedForeground1
  },
  notesCell: {
    maxWidth: "260px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  actionsCell: {
    whiteSpace: "nowrap"
  }
});

// Dates from the API arrive as YYYY-MM-DD with no time component. Parsing them
// with `new Date(iso)` treats them as midnight UTC, which renders as the prior
// day in any west-of-UTC timezone. Pin the formatter to UTC so the day number
// stays consistent regardless of viewer timezone.
function formatDateRange(startIso: string, endIso: string): string {
  const fmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  if (startIso === endIso) return fmt.format(new Date(startIso));
  const startD = new Date(startIso);
  const endD = new Date(endIso);
  if (startD.getUTCFullYear() === endD.getUTCFullYear()) {
    const startShort = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(startD);
    return `${startShort} – ${fmt.format(endD)}`;
  }
  return `${fmt.format(startD)} – ${fmt.format(endD)}`;
}

function formatDays(r: LeaveRequest): string {
  if (r.halfDay) return `½ day${r.amPm ? ` (${r.amPm})` : ""}`;
  return r.workDays === 1 ? "1 day" : `${r.workDays} days`;
}

function statusBadge(r: LeaveRequest) {
  switch (r.status) {
    case "approved":
      return <Badge appearance="filled" color="success">Approved</Badge>;
    case "pending":
      return <Badge appearance="filled" color="warning">Pending</Badge>;
    case "denied":
      return <Badge appearance="filled" color="danger">Denied</Badge>;
    case "cancelled":
      return <Badge appearance="filled" color="severe">Cancelled</Badge>;
    case "draft":
    default:
      return <Badge appearance="outline" color="informative">Draft</Badge>;
  }
}

export default function MyRequests() {
  const styles = useStyles();
  const toasterId = useId("requests-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const [searchParams] = useSearchParams();
  const userIdParam = searchParams.get("userId");
  const [data, setData] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warming, setWarming] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  // Bumped after every create/cancel so BalancePanel re-fetches the new totals.
  const [balanceVersion, setBalanceVersion] = useState(0);

  // Treat any explicit ?userId= as "viewing other" for copy purposes.
  const viewingOther = userIdParam !== null;
  const heading = viewingOther && data ? `Requests for ${data.userDisplayName}` : "My requests";

  const loadRequests = useCallback(() => {
    setError(null);
    const qs = userIdParam ? `?userId=${encodeURIComponent(userIdParam)}` : "";
    return fetchWithRetry(`/api/leave-requests${qs}`, undefined, { onWarming: () => setWarming(true) })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as ApiResponse;
      })
      .then((d) => { setData(d); setWarming(false); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [userIdParam]);

  useEffect(() => {
    setData(null);
    setWarming(false);
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    fetchWithRetry("/api/leave-types", undefined, { onWarming: () => setWarming(true) })
      .then(async (r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setLeaveTypes(d.leaveTypes as LeaveTypeOption[]))
      .catch(() => {
        // Non-fatal — without the list, the New request button stays disabled.
      });
  }, []);

  async function cancelRequest(r: LeaveRequest) {
    setCancellingId(r.id);
    try {
      const res = await fetch(`/api/leave-requests/${encodeURIComponent(r.id)}/cancel`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      dispatchToast(
        <Toast><ToastTitle>Cancelled</ToastTitle><ToastBody>Request #{r.requestNumber} was cancelled.</ToastBody></Toast>,
        { intent: "success" }
      );
      setBalanceVersion((v) => v + 1);
      await loadRequests();
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>Couldn't cancel request</ToastTitle><ToastBody>{e instanceof Error ? e.message : String(e)}</ToastBody></Toast>,
        { intent: "error" }
      );
    } finally {
      setCancellingId(null);
    }
  }

  const canCreate = !viewingOther && leaveTypes.length > 0;

  if (error) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>{heading}</Title2>
        <BalancePanel userIdParam={userIdParam} version={balanceVersion} />
        <Body1 as="p" className={styles.error}>Error loading requests: {error}</Body1>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>{heading}</Title2>
        <BalancePanel userIdParam={userIdParam} version={balanceVersion} />
        <Spinner
          size="tiny"
          label={warming ? "Waking up the database… 30–60s the first time after hours." : "Loading…"}
        />
      </div>
    );
  }

  const showNewButton = !viewingOther;

  return (
    <div>
      <Toaster toasterId={toasterId} />
      <div className={styles.headerRow}>
        <div>
          <Title2 as="h2" block>{heading}</Title2>
          {data.requests.length > 0 && (
            <Body1 as="p">
              {data.requests.length} request{data.requests.length === 1 ? "" : "s"} for {data.userDisplayName}
            </Body1>
          )}
        </div>
        {showNewButton && (
          <Button
            appearance="primary"
            onClick={() => setDrawerOpen(true)}
            disabled={!canCreate}
            title={canCreate ? undefined : "Loading leave types…"}
          >
            New request
          </Button>
        )}
      </div>

      <BalancePanel userIdParam={userIdParam} version={balanceVersion} />

      {data.requests.length === 0 ? (
        <div className={styles.empty}>
          <Body1>{viewingOther ? `${data.userDisplayName} has no leave requests.` : "You have no leave requests yet."}</Body1>
        </div>
      ) : (
        <Table aria-label="Leave requests">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>#</TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Dates</TableHeaderCell>
              <TableHeaderCell>Days</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Notes</TableHeaderCell>
              {!viewingOther && <TableHeaderCell aria-label="Actions" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.requests.map((r) => {
              const cancellable = !viewingOther && (r.status === "pending" || r.status === "approved");
              return (
                <TableRow key={r.id}>
                  <TableCell>{r.requestNumber}</TableCell>
                  <TableCell>{r.leaveTypeName}</TableCell>
                  <TableCell>{formatDateRange(r.startDate, r.endDate)}</TableCell>
                  <TableCell>{formatDays(r)}</TableCell>
                  <TableCell>{statusBadge(r)}</TableCell>
                  <TableCell className={styles.notesCell} title={r.notes ?? ""}>{r.notes ?? ""}</TableCell>
                  {!viewingOther && (
                    <TableCell className={styles.actionsCell}>
                      {cancellable && (
                        <Dialog>
                          <DialogTrigger disableButtonEnhancement>
                            <Button size="small" appearance="subtle" disabled={cancellingId === r.id}>
                              {cancellingId === r.id ? "Cancelling…" : "Cancel"}
                            </Button>
                          </DialogTrigger>
                          <DialogSurface>
                            <DialogBody>
                              <DialogTitle>Cancel request #{r.requestNumber}?</DialogTitle>
                              <DialogContent>
                                {r.leaveTypeName} · {formatDateRange(r.startDate, r.endDate)} · {formatDays(r)}.
                                {" "}This will reverse the balance hit and write an audit entry. The action can't be undone, but you can submit a new request.
                              </DialogContent>
                              <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                  <Button appearance="secondary">Keep it</Button>
                                </DialogTrigger>
                                <DialogTrigger disableButtonEnhancement>
                                  <Button appearance="primary" onClick={() => cancelRequest(r)}>Yes, cancel</Button>
                                </DialogTrigger>
                              </DialogActions>
                            </DialogBody>
                          </DialogSurface>
                        </Dialog>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <NewRequestDrawer
        open={drawerOpen}
        leaveTypes={leaveTypes}
        onClose={() => setDrawerOpen(false)}
        onCreated={() => {
          dispatchToast(
            <Toast><ToastTitle>Request submitted</ToastTitle></Toast>,
            { intent: "success" }
          );
          setBalanceVersion((v) => v + 1);
          loadRequests();
        }}
      />
    </div>
  );
}
